import React, { useState, useCallback, memo } from 'react';

interface UploadDocumentModalProps {
    isOpen: boolean;
    onClose: () => void;
    onUpload: (formData: UploadDocFormData) => Promise<void>;
    existingDocuments: any[];
    darkMode?: boolean;
}

export interface UploadDocFormData {
    file: File | null;
    type: 'new' | 'update';
    reason: string;
    changes: string;
    updated_from: string;
}

const UploadDocumentModal: React.FC<UploadDocumentModalProps> = memo(({
    isOpen,
    onClose,
    onUpload,
    existingDocuments,
    darkMode = false
}) => {
    const [formData, setFormData] = useState<UploadDocFormData>({
        file: null,
        type: 'new',
        reason: '',
        changes: '',
        updated_from: ''
    });
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');

    const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            setFormData(prev => ({ ...prev, file: e.target.files![0] }));
        }
    }, []);

    const handleTypeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        setFormData(prev => ({
            ...prev,
            type: e.target.value as 'new' | 'update',
            changes: '',
            updated_from: ''
        }));
    }, []);

    const handleReasonChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setFormData(prev => ({ ...prev, reason: e.target.value }));
    }, []);

    const handleChangesChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setFormData(prev => ({ ...prev, changes: e.target.value }));
    }, []);

    const handleUpdatedFromChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        setFormData(prev => ({ ...prev, updated_from: e.target.value }));
    }, []);

    const handleClose = useCallback(() => {
        setFormData({
            file: null,
            type: 'new',
            reason: '',
            changes: '',
            updated_from: ''
        });
        setError('');
        onClose();
    }, [onClose]);

    const handleSubmit = useCallback(async () => {
        if (!formData.file || !formData.reason) return;

        setUploading(true);
        setError('');
        try {
            await onUpload(formData);
            handleClose();
        } catch (err: any) {
            setError(err.message || 'Gagal mengupload dokumen');
        } finally {
            setUploading(false);
        }
    }, [formData, onUpload, handleClose]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className={`w-full max-w-lg rounded-lg shadow-xl ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                <div className={`flex items-center justify-between p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        Tambah Dokumen Pendukung
                    </h3>
                    <button
                        onClick={handleClose}
                        className={`p-2 rounded-full transition-colors ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`}
                    >
                        <i className="fa-solid fa-times"></i>
                    </button>
                </div>

                <div className="p-4 space-y-4">
                    {/* File Input */}
                    <div>
                        <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            File <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="file"
                            onChange={handleFileChange}
                            className={`w-full px-3 py-2 border rounded-lg text-sm ${darkMode
                                ? 'bg-gray-700 border-gray-600 text-gray-300'
                                : 'border-gray-300 bg-gray-50'
                                }`}
                        />
                        {formData.file && (
                            <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                Selected: {formData.file.name} ({(formData.file.size / 1024 / 1024).toFixed(2)} MB)
                            </p>
                        )}
                    </div>

                    {/* Type */}
                    <div>
                        <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Type Dokumen <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={formData.type}
                            onChange={handleTypeChange}
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent text-sm ${darkMode
                                ? 'bg-gray-700 border-gray-600 text-gray-300'
                                : 'border-gray-300 bg-gray-50'
                                }`}
                        >
                            <option value="new">Baru</option>
                            <option value="update">Update dari dokumen sebelumnya</option>
                        </select>
                    </div>

                    {/* Updated From (only if type is update) */}
                    {formData.type === 'update' && (
                        <div>
                            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                Update dari Dokumen <span className="text-red-500">*</span>
                            </label>
                            <select
                                value={formData.updated_from}
                                onChange={handleUpdatedFromChange}
                                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent text-sm ${darkMode
                                    ? 'bg-gray-700 border-gray-600 text-gray-300'
                                    : 'border-gray-300 bg-gray-50'
                                    }`}
                            >
                                <option value="">-- Pilih Dokumen --</option>
                                {existingDocuments.map((doc: any, idx: number) => {
                                    const docName = doc.file_name || doc.name || `Document ${idx + 1}`;
                                    return (
                                        <option key={idx} value={docName}>{docName}</option>
                                    );
                                })}
                            </select>
                        </div>
                    )}

                    {/* Reason */}
                    <div>
                        <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Alasan <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            value={formData.reason}
                            onChange={handleReasonChange}
                            placeholder="Masukkan alasan mengupload file ini"
                            rows={3}
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none text-sm ${darkMode
                                ? 'bg-gray-700 border-gray-600 text-gray-300 placeholder-gray-500'
                                : 'border-gray-300'
                                }`}
                            required
                        />
                    </div>

                    {/* Changes (only if type is update) */}
                    {formData.type === 'update' && (
                        <div>
                            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                Perubahan <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                value={formData.changes}
                                onChange={handleChangesChange}
                                placeholder="Masukkan informasi perubahan yang dilakukan dari dokumen sebelumnya"
                                rows={4}
                                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none text-sm ${darkMode
                                    ? 'bg-gray-700 border-gray-600 text-gray-300 placeholder-gray-500'
                                    : 'border-gray-300'
                                    }`}
                                required
                            />
                        </div>
                    )}

                    {/* Error Message */}
                    {error && (
                        <div className={`p-3 rounded-lg ${darkMode ? 'bg-red-900/50 border border-red-700' : 'bg-red-50 border border-red-200'}`}>
                            <p className={`text-sm ${darkMode ? 'text-red-300' : 'text-red-700'}`}>{error}</p>
                        </div>
                    )}

                    {/* Actions */}
                    <div className={`flex items-center justify-end space-x-3 pt-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                        <button
                            type="button"
                            onClick={handleClose}
                            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${darkMode
                                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                        >
                            Batal
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={uploading || !formData.file || !formData.reason}
                            className="px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                        >
                            {uploading ? (
                                <>
                                    <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                                    Mengupload...
                                </>
                            ) : (
                                <>
                                    <i className="fa-solid fa-upload mr-2"></i>
                                    Upload
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
});

UploadDocumentModal.displayName = 'UploadDocumentModal';

export default UploadDocumentModal;
